import { UserRoutes } from '~config/routes'

export const SUPPORT_EMAIL = 'support@chckvet.com'

/**
 * A single suggested next step for an auth error. Either an in-app `href`
 * (e.g. reset password) or a `mailto` link to support.
 */
export type AuthErrorAction = {
    label: string
    href?: string
    mailto?: string
}

/**
 * Structured info used to render a "stupid obvious" auth error UI:
 * a clear headline + explanation + up to two suggested next steps.
 */
export type AuthErrorInfo = {
    title: string
    description: string
    primaryAction?: AuthErrorAction
    secondaryAction?: AuthErrorAction
}

const SUPPORT_ACTION: AuthErrorAction = {
    label: `Email ${SUPPORT_EMAIL}`,
    mailto: SUPPORT_EMAIL,
}

const RESET_PASSWORD_ACTION: AuthErrorAction = {
    label: 'Reset your password',
    href: UserRoutes.ForgotPassword,
}

/**
 * Maps a Firebase client SDK auth error (from `signInWithEmailAndPassword`)
 * to actionable UI info. `firebaseErrorCode` is `error.code` on the thrown
 * Firebase error, e.g. `'auth/invalid-credential'`.
 */
export function getLoginErrorInfo(
    firebaseErrorCode: string | undefined,
): AuthErrorInfo {
    switch (firebaseErrorCode) {
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
        case 'auth/invalid-login-credentials':
            return {
                title: 'Email or password is incorrect',
                description:
                    "Forgot your password? You can reset it. If you keep getting stuck, our support team can help.",
                primaryAction: RESET_PASSWORD_ACTION,
                secondaryAction: SUPPORT_ACTION,
            }
        case 'auth/user-not-found':
            return {
                title: 'No account found with this email',
                description:
                    "Make sure you're using the email your team invited you with. If you didn't receive an invite, ask a teammate or reach out to support.",
                primaryAction: SUPPORT_ACTION,
            }
        case 'auth/too-many-requests':
            return {
                title: 'Too many sign-in attempts',
                description:
                    "For your security, sign-in is paused for a few minutes after too many failed attempts. Try again shortly, or reset your password.",
                primaryAction: RESET_PASSWORD_ACTION,
            }
        case 'auth/user-disabled':
            return {
                title: 'This account has been disabled',
                description:
                    "Please reach out to our support team and we'll help you get back in.",
                primaryAction: SUPPORT_ACTION,
            }
        case 'auth/network-request-failed':
            return {
                title: 'Network error',
                description:
                    "We couldn't reach our servers. Check your internet connection and try again.",
            }
        default:
            return {
                title: "We couldn't sign you in",
                description:
                    "Double-check your email and password, or reset your password if you're stuck. Still not working? Contact our support team.",
                primaryAction: RESET_PASSWORD_ACTION,
                secondaryAction: SUPPORT_ACTION,
            }
    }
}

/**
 * Maps a Firebase client SDK auth error (from `sendPasswordResetEmail`) to
 * actionable UI info. Note that for security Firebase doesn't reliably
 * surface "user not found" — most errors here are network/validation.
 */
export function getPasswordResetErrorInfo(
    firebaseErrorCode: string | undefined,
): AuthErrorInfo {
    switch (firebaseErrorCode) {
        case 'auth/invalid-email':
            return {
                title: "That email address doesn't look right",
                description: 'Please double-check the email and try again.',
            }
        case 'auth/network-request-failed':
            return {
                title: 'Network error',
                description:
                    "We couldn't reach our servers. Check your internet connection and try again.",
            }
        default:
            return {
                title: "We couldn't send the reset email",
                description:
                    'Please try again. If the problem keeps happening, our support team can help.',
                primaryAction: SUPPORT_ACTION,
            }
    }
}
